import type { Filter as FilterType, SortParams } from '../types/SortAndFilter';
import { getInitialQueryParams } from '../utils/filters';
import type { QueryParams } from '../utils/url';
import { dispatchTrackingEventGA4 } from './dispatchTrackingEventGA4';

const enum Event {
  CategoryFiltersDrawerClick = 'category-filters-drawer_click',
  CategoryFiltersClick = 'category-filters_click',
  CategorySortOptionsClick = 'category-sort-options_click',
}

const enum Element {
  Accordion = 'accordion',
  Dropdown = 'dropdown',
  Button = 'button',
  Filter = 'filter',
}

const enum Interaction {
  OpenSortOptions = 'Open sort options',
  ApplySort = 'Apply sort',
  ApplyFilter = 'Apply filters',
  ApplyRange = 'Apply range',
  ExpandFilter = 'Expand filter',
  CollapseFilter = 'Collapse filter',
  CloseDrawer = 'Close drawer',
  OpenDrawer = 'Open drawer',
  FilterChanged = 'Change filter',
  FilterAdded = 'Add filter',
  FilterRemoved = 'Remove filter',
}

const ALL_VALUES = 'All values';

const RANGE_FILTER_NAME_REGEX = /\.min|\.max/g;

export const trackOpenSortOptions = (isDrawer = false) =>
  dispatchTrackingEventGA4({
    event: isDrawer ? Event.CategoryFiltersDrawerClick : Event.CategorySortOptionsClick,
    element: isDrawer ? Element.Accordion : Element.Dropdown,
    interaction: Interaction.OpenSortOptions,
  });

interface ChangeSortOptionParams {
  selectedOrder: string;
  sortOptions: SortParams['sortOptions'];
}

export const trackChangingSortOption = ({ selectedOrder, sortOptions }: ChangeSortOptionParams) => {
  const selectedOption = sortOptions.find(({ option }) => option === selectedOrder);

  dispatchTrackingEventGA4({
    event: Event.CategorySortOptionsClick,
    element: Element.Dropdown,
    interaction: Interaction.ApplySort,
    list_item: selectedOrder,
    list_position: selectedOption ? sortOptions.indexOf(selectedOption) : 0,
  });
};

interface ToggleFilterParams {
  filter: string;
  isExpanded: boolean;
  isDrawer?: boolean;
}

export const trackToggleFilter = ({ filter, isExpanded, isDrawer = false }: ToggleFilterParams) => {
  dispatchTrackingEventGA4({
    event: isDrawer ? Event.CategoryFiltersDrawerClick : Event.CategoryFiltersClick,
    element: Element.Accordion,
    interaction: isExpanded ? Interaction.CollapseFilter : Interaction.ExpandFilter,
    label: filter,
  });
};

export const trackToggleSortOptions = (isExpanded: boolean) => {
  trackToggleFilter({ filter: 'order', isExpanded, isDrawer: true });
};

export const trackApplyFilters = (url: string) => {
  const filters = [];
  const search = new URL(url).search;
  for (const key of new URLSearchParams(search).keys()) {
    const filterName = key.replace(RANGE_FILTER_NAME_REGEX, '');
    if (filters.indexOf(filterName) === -1) {
      filters.push(filterName);
    }
  }

  dispatchTrackingEventGA4({
    event: Event.CategoryFiltersDrawerClick,
    element: Element.Button,
    interaction: Interaction.ApplyFilter,
    list_items: filters,
    list_length: filters.length,
  });
};

const getAddedValue = (previousValue: string, newValue: string) => {
  const prevValues = previousValue ? previousValue.split('|') : [];
  const newValues = newValue ? newValue.split('|') : [];

  if (newValues.length - prevValues.length > 1) {
    return ALL_VALUES;
  }

  return newValues.filter(value => !prevValues.includes(value))?.[0];
};

const getRemovedValue = (previousValue: string, newValue: string) => {
  const prevValues = previousValue ? previousValue.split('|') : [];
  const newValues = newValue ? newValue.split('|') : [];

  if (prevValues.length - newValues.length > 1) {
    return ALL_VALUES;
  }

  return prevValues.filter(value => !newValues.includes(value))?.[0];
};

const isChangeInteraction = (previousValue: string, value: string) =>
  previousValue.length > 0 &&
  value.length > 0 &&
  !value.includes('|') &&
  !previousValue.includes('|');

const getValues = (prevQueryParams: QueryParams, queryParams: QueryParams) => {
  const [label] = Object.keys(queryParams);

  // We track range filters separately
  if (label.match(RANGE_FILTER_NAME_REGEX)) {
    return false;
  }

  const previousValue = prevQueryParams[label];
  const value = queryParams[label];

  if (previousValue === value || typeof previousValue !== 'string' || typeof value !== 'string') {
    return false;
  }

  return { label, previousValue, value };
};

const getFilterDifference = (prevQueryParams: QueryParams, queryParams: QueryParams) => {
  const values = getValues(prevQueryParams, queryParams);
  if (!values) {
    return;
  }
  const { label, previousValue, value } = values;

  if (isChangeInteraction(previousValue, value)) {
    return {
      interaction: Interaction.FilterChanged,
      label,
      value,
    };
  }
  if (value.length > previousValue.length) {
    return {
      interaction: Interaction.FilterAdded,
      label,
      value: getAddedValue(previousValue, value),
    };
  }

  return {
    interaction: Interaction.FilterRemoved,
    label,
    value: getRemovedValue(previousValue, value),
  };
};

export const trackDrawerFilterChange = (prevQueryParams: QueryParams, queryParams: QueryParams) => {
  const difference = getFilterDifference(prevQueryParams, queryParams);
  if (difference) {
    dispatchTrackingEventGA4({
      event: Event.CategoryFiltersDrawerClick,
      element: Element.Filter,
      ...difference,
    });
  }
};

export const trackFilterChange = (filters: FilterType[], queryParams: QueryParams) => {
  const { page, ...prevQueryParams } = getInitialQueryParams(filters);
  const difference = getFilterDifference(prevQueryParams, queryParams);
  if (difference) {
    dispatchTrackingEventGA4({
      event: Event.CategoryFiltersClick,
      element: Element.Filter,
      ...difference,
    });
  }
};

export const enum RangeSliderElement {
  Slider = 'rc-slider',
  Input = 'input',
}

export const enum RangeSliderInteraction {
  Min = 'Change min',
  Max = 'Change max',
}

interface RangeSliderChangeParams {
  element: RangeSliderElement;
  interaction: RangeSliderInteraction;
  label: string;
  isDrawer?: boolean;
}

export const trackRangeSliderChange = ({ isDrawer, ...params }: RangeSliderChangeParams) =>
  dispatchTrackingEventGA4({
    event: isDrawer ? Event.CategoryFiltersDrawerClick : Event.CategoryFiltersClick,
    ...params,
  });

export const trackRangeSliderApply = (label: string) =>
  dispatchTrackingEventGA4({
    event: Event.CategoryFiltersClick,
    element: Element.Button,
    interaction: Interaction.ApplyRange,
    label,
  });

export const trackCollapsibleChange = (label: string, expanded: boolean) =>
  dispatchTrackingEventGA4({
    event: Event.CategoryFiltersClick,
    interaction: expanded ? 'Show more' : 'Show less',
    label,
  });

export const trackFilterDrawerClose = () =>
  dispatchTrackingEventGA4({
    event: Event.CategoryFiltersDrawerClick,
    interaction: Interaction.CloseDrawer,
  });

export const trackSearchInputClick = (filterName: string) =>
  dispatchTrackingEventGA4({
    event: Event.CategoryFiltersDrawerClick,
    element: 'search input',
    interaction: 'search',
    label: filterName,
  });

export const trackClearSearchClick = (filterName: string) =>
  dispatchTrackingEventGA4({
    event: Event.CategoryFiltersDrawerClick,
    element: Element.Button,
    interaction: 'cancel search',
    label: filterName,
  });
